'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { MarkDown } from './atoms/MarkDown';

function joinClasses(...values) {
    return values.filter(Boolean).join(' ');
}

function omitMarkdownDomProps(props = {}) {
    const { node, ordered, index, checked, sourcePosition, ...domProps } =
        props;
    void node;
    void ordered;
    void index;
    void checked;
    void sourcePosition;
    return domProps;
}

export default function TermsAndConditions(props) {
    const {
        Tagline,
        Header,
        DocumentContent,
        ColorScheme = 'white',
        VerticalSpacing = 'medium',
    } = props;

    const { backgroundColor, textColor, headerColor, backgroundStyle } =
        getColorScheme(ColorScheme, props.GradientDirection);
    const vSpace = getVerticalSpacing(VerticalSpacing);
    const documentContent = String(DocumentContent || '').trim();

    const legalMarkdownComponents = {
        h2: ({ className, children, ...rest }) => (
            <h2
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `mb-4 mt-12 font-display text-2xl tracking-tight ${headerColor} md:text-3xl`
                )}
            >
                {children}
            </h2>
        ),
        h3: ({ className, children, ...rest }) => (
            <h3
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `mb-3 mt-8 font-display text-xl tracking-tight ${headerColor} md:text-2xl`
                )}
            >
                {children}
            </h3>
        ),
        h4: ({ className, children, ...rest }) => (
            <h4
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `mb-3 mt-6 font-display text-base font-semibold tracking-wide ${headerColor} md:text-lg`
                )}
            >
                {children}
            </h4>
        ),
        p: ({ className, children, ...rest }) => (
            <p
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `mb-4 whitespace-pre-wrap text-base font-text leading-7 ${textColor} last:mb-0`
                )}
            >
                {children}
            </p>
        ),
        ul: ({ className, children, ...rest }) => (
            <ul
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `mt-4 list-disc space-y-2 pl-6 text-base font-text leading-7 ${textColor}`
                )}
            >
                {children}
            </ul>
        ),
        ol: ({ className, children, ...rest }) => (
            <ol
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `mt-4 list-decimal space-y-2 pl-6 text-base font-text leading-7 ${textColor}`
                )}
            >
                {children}
            </ol>
        ),
        li: ({ className, children, ...rest }) => (
            <li
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(className, 'pl-1 marker:font-semibold')}
            >
                {children}
            </li>
        ),
        strong: ({ className, children, ...rest }) => (
            <strong
                {...omitMarkdownDomProps(rest)}
                className={joinClasses(
                    className,
                    `font-semibold ${headerColor}`
                )}
            >
                {children}
            </strong>
        ),
    };

    return (
        <section
            className={`editable-component relative ${backgroundColor} ${vSpace} px-6 lg:px-8`}
            style={{
                ...backgroundStyle,
                marginTop: '-172px',
                '@media (minWidth: 768px)': { marginTop: '-70px' },
            }}
            data-json="termsandconditions"
        >
            <div
                className={`relative z-40 mx-auto max-w-4xl px-6 pt-20 text-base leading-7 ${textColor} lg:px-8 lg:pt-24`}
            >
                <MarkDown
                    variant="p"
                    className={`font-display text-base uppercase leading-7 tracking-wide ${headerColor}`}
                >
                    {Tagline}
                </MarkDown>

                <MarkDown
                    variant="h1"
                    className={`mt-2 font-display text-2xl tracking-tight ${headerColor} md:text-3xl lg:text-4xl`}
                >
                    {Header}
                </MarkDown>

                <MarkDown
                    className="mt-10"
                    components={legalMarkdownComponents}
                >
                    {documentContent}
                </MarkDown>
            </div>
        </section>
    );
}
